// Q Help: the one in-app support widget every MHub product loads (mhub-web-turborepo,
// apps/mhub-q-help-widget). It shows Q's in-app chat. Bitrix24 is cancelled (26 October 2026)
// and there is no form fallback: a customer Q doesn't admit, or can't reach, gets the widget's
// contact card (WhatsApp, phone, email) — so Showroom no longer mounts a Bitrix form of its own.
// The widget is a prebuilt script with its own UI in a shadow root; the app only adds the tag,
// once per page, and keeps data-lang in step with its own language.

import Auth from '@aws-amplify/auth';

// The marker @mhub/q-help (the monorepo apps' injector) uses too, so a page never gets two tags.
const SCRIPT_ATTR = 'data-mhub-q-help-loader';
const PROJECT = 'showroom';

// Showroom's languages (src/constants/languages.js) as the widget's regional codes. The widget
// also speaks ms-MY, which Showroom does not offer.
const WIDGET_LANGUAGES = {
  en: 'en-GB',
  id: 'id-ID',
  vi: 'vi-VN',
};

// The widget code for the language the app is showing. Anything Showroom has no locale for is
// shown in English (i18n fallbackLng), so the widget follows suit. Before i18next has settled on
// a language there is nothing to pass: the widget then reads the shared cookie, then the browser,
// until the first languageChanged sets the attribute.
export function widgetLanguage(language) {
  if (typeof language !== 'string' || !language) return null;
  const code = language.toLowerCase().split(/[-_]/)[0];
  return WIDGET_LANGUAGES[code] || WIDGET_LANGUAGES.en;
}

// The widget reads the session from the shared Cognito cookies. It asks the app for a fresh id
// token, once, on open or send (never while idle), when the one there is within 30 s of expiry or
// Q refused it, so the refresh is forced: currentSession() would hand back a token it still
// calls valid. refreshSession() renews it with the refresh token and writes the new cookies on
// the shared domain, as currentSession() does for every API call (fetchM). Signed out, or a
// devstack persona: it rejects, and the widget asks to refresh the page or sign in. Sign in
// reloads, so Showroom's own 401 handling sends the user to log in (ADFS included) and back.
const qHelpIdToken = async () => {
  const user = await Auth.currentAuthenticatedUser();
  const signedIn = user && typeof user.getSignInUserSession === 'function'
    ? user.getSignInUserSession() : null;
  if (!signedIn || typeof user.refreshSession !== 'function') {
    return (await Auth.currentSession()).getIdToken().getJwtToken();
  }
  const session = await new Promise((resolve, reject) => {
    user.refreshSession(signedIn.getRefreshToken(), (error, fresh) => (
      error ? reject(error) : resolve(fresh)
    ));
  });
  return session.getIdToken().getJwtToken();
};

// Set before the tag goes in, so the widget finds it when it boots; kept on an object the widget
// already made (Root remounting under hot reload).
function setTokenProvider() {
  const help = window.MHubQHelp || {};
  help.tokenProvider = qHelpIdToken;
  window.MHubQHelp = help;
}

function findScript(doc) {
  return doc.querySelector(`script[${SCRIPT_ATTR}]`);
}

// Adds the widget's script tag once. A second call (Root remounting under hot reload) returns
// the tag already on the page. No URL configured for this environment: no tag at all.
export function mountQHelpWidget({ src, language, version }, doc = document) {
  if (!src) return null;
  setTokenProvider();
  const existing = findScript(doc);
  if (existing) return existing;
  const script = doc.createElement('script');
  script.src = src;
  script.defer = true;
  script.setAttribute(SCRIPT_ATTR, '');
  script.setAttribute('data-project', PROJECT);
  const lang = widgetLanguage(language);
  if (lang) script.setAttribute('data-lang', lang);
  if (version) script.setAttribute('data-version', version);
  doc.head.appendChild(script);
  return script;
}

// Follows the app's language. The widget reads data-lang when it boots and watches the attribute
// afterwards, so updating the tag is enough whether or not it has loaded yet.
export function setQHelpWidgetLanguage(language, doc = document) {
  const script = findScript(doc);
  const lang = widgetLanguage(language);
  if (script && lang && script.getAttribute('data-lang') !== lang) {
    script.setAttribute('data-lang', lang);
  }
}



// WEBPACK FOOTER //
// ./src/utils/qHelpWidget.js